import React, { useState, useEffect, FC } from 'react';
import { Box, Button, Icon, ButtonAppearance, ButtonSize, IconSize, Spinner, SpinnerSize } from '@bechtel/gds';
import { DownloadUserListCSVData, DownloadUsersListProps } from './types';
import { UserList, SortOrder, UserSortByTerm, User, PagingInput, UserFilterInput } from '@bechtel/types';
import { exportCSVFile, ErrorRetry } from '@bechtel/client-common';
import { forEach, get, map } from 'lodash';
import { GET_USERS_BY_ROLE } from '@bechtel/client-gql';
import { useLazyQuery, NetworkStatus } from '@apollo/client';
import { t, Trans } from '@lingui/macro';

const headers = {
  firstName: t`First Name`,
  lastName: t`Last Name`,
  bun: t`Bun`,
  etrackId: t`Etrack Id`,
  roles: t`Roles`,
  phoneNumber: t`Phone Number`,
  mobileCarrier: t`Phone Carrier`,
  email: t`Email Id`,
  subGroupDescription: t`Job Title`,
  disciplines: t`Disciplines`,
};

export const DownloadUsersList: FC<DownloadUsersListProps> = ({ numUsers, roleId, roleName }) => {
  const [startDownloadUserList, setStartDownloadUserList] = useState(false);
  const usersLimit = 20;
  const [loadUsers, { loading, error, data, refetch, fetchMore, networkStatus }] = useLazyQuery<
    { users: UserList },
    {
      paging: PagingInput;
      input: UserFilterInput;
    }
  >(GET_USERS_BY_ROLE, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const userList: UserList['items'] = get(data, 'users.items', []);
  const totalPages = Math.ceil(get(data, 'users.total', 0) / usersLimit);
  let userListCursor = get(data, 'users.cursor', 1);

  const startUserListDownload = () => {
    loadUsers({
      variables: {
        paging: {
          cursor: userListCursor.toString(),
          limit: usersLimit,
        },
        input: {
          filterByCompanyIds: [],
          filterByFacilityIds: [],
          searchKeyword: '',
          sortBy: UserSortByTerm.LAST_NAME,
          sortOrder: SortOrder.DESC,
          filterByRoleIds: [roleId],
        },
      },
    });
  };

  const fetchMoreData = async () => {
    const userData = await fetchMore({
      variables: {
        paging: {
          cursor: userListCursor.toString(),
          limit: usersLimit,
        },
      },
    });
    if (userData == undefined) return;
    const { data } = userData;
    return get(data, 'users.cursor', 1);
  };

  const fetchUserList = async () => {
    let hasNextPage = userListCursor < totalPages + 1;
    while (hasNextPage) {
      userListCursor = await fetchMoreData();
      hasNextPage = userListCursor < totalPages + 1;
    }
  };

  useEffect(() => {
    if (!startDownloadUserList || !userList || userList.length === 0) return;
    if (startDownloadUserList && userList.length > 0) fetchUserList();

    const downloadUserListData: DownloadUserListCSVData[] = [];
    const downloadHeaders = { ...headers };
    forEach(userList, (item: User) => {
      const userRole = map(item.roles, (items) => items.name);
      const userDisciplines = map(item.disciplines, (items) => items.name);
      const downloadUserListItem: DownloadUserListCSVData = {
        firstName: item.firstName ? item.firstName : '',
        lastName: item.lastName ? item.lastName : '',
        bun: item.bun ? item.bun : '',
        etrackId: item.etrackId ? item.etrackId : '',
        roles: userRole.join(','),
        phoneNumber: item.phoneNumber ? item.phoneNumber : '',
        mobileCarrier: item.mobileCarrier ? item.mobileCarrier : '',
        email: item.email ? item.email : '',
        subGroupDescription: item?.crewMember?.subGroupDescription ? item?.crewMember?.subGroupDescription : '',
        disciplines: userDisciplines.join(','),
      };
      downloadUserListData.push(downloadUserListItem);
    });
    const fileName = roleName;
    if(userListCursor == totalPages + 1){
      exportCSVFile(downloadHeaders, downloadUserListData, fileName);
      setStartDownloadUserList(false);
    }
  }, [userList, startDownloadUserList]);

  return (
    <Box mr={{ sm: '400' }} style={!numUsers || numUsers == 0 ? { opacity: 0.4, pointerEvents: 'none' } : {}}>
      <Button
        size={ButtonSize.SMALL}
        appearance={ButtonAppearance.SECONDARY}
        onClick={(e) => {
          setStartDownloadUserList(true);
          startUserListDownload();
          e.preventDefault();
        }}
      >
        {(loading || networkStatus === NetworkStatus.refetch) && (
          <Box mr={{ sm: '400' }} alignItems={{ sm: 'center' }} data-testid="rolebyids-loading">
            <Spinner size={SpinnerSize.MEDIUM} />
          </Box>
        )}
        {!loading && error && (
          <div data-testid="roles-by-ids-retry">
            <ErrorRetry refetch={refetch}>
              <Trans>There was a problem loading users list. Please click the button below to try again.</Trans>
            </ErrorRetry>
          </div>
        )}
        {!loading && <Icon icon="download" size={IconSize.SMALL} fillColor="blue.800" />}
      </Button>
    </Box>
  );
};
